import React, { useEffect, useMemo, useRef, useState } from "react";
import annualOkrLiveReviewStore, {
  useAnnualOkrLiveReviewStore,
} from "./_socket/AnnualOkrLiveReviewSocketStore";
import { buildLiveAgenda } from "./_socket/annualOkrLiveAgenda";
// Same relative path in both repos — see the note at the top of that file.
import ObjectiveDetails from "../_shared/ObjectiveDetails";
import WigLinkChip from "../_shared/WigLinkChip";
import DimensionPill from "../_shared/DimensionPill";
// The interview's own owner picker — same component, and the same relative
// path in both repos, so this import survives the byte-identical rule.
import StandardSelect from "../../../shared/formComponents/StandardSelect/StandardSelect";
import EbDialog from "../../ExecutionBlueprint/ExecutionBlueprintLiveReview/_shared/EbDialog";

// ═════════════════════════════════════════════════════════════════════════════
// Annual OKR — Live Review body
//
// ⚠️ THIS FILE IS INTENTIONALLY IDENTICAL IN TWO REPOSITORIES, for the same
// reason the socket store beside it is: the CEO and the coach are looking at
// this screen at the same moment, in a meeting, and any divergence between the
// two renderings is a bug that surfaces as them describing different things to
// each other. `diff` is the test. Both copies sit at the same folder depth so
// the relative import above matches.
//
//   10x-coach-frontend/.../CoachPanel/AnnualOkr/AnnualOkrLiveReview/
//   10x-frontend/.../UserPanel/AnnualOkr/AnnualOkrLiveReview/
//
// The ONE permitted difference is passed in: `viewerRole`. Per §7.4 both
// parties can add, edit, delete and set status — "no role restriction beyond
// having joined" — so `viewerRole` drives the presence strip's "(you)" and
// nothing else. Do not add a second branch; put the difference in a prop.
//
// ⚠️ IT USES THE EXECUTION BLUEPRINT'S OWN `eblr-*` CLASSES, not a local set.
// A first pass invented `aolr-okr`, `aolr-okr-btn`, `aolr-agenda-item` and the
// rest, and the screen rendered as unstyled native buttons and default list
// markers, because nothing styled them. These come from
// `ExecutionBlueprintLiveReview.scss`, imported below, so the row chrome, the
// status buttons, the agenda and the presence chips are identical to the
// blueprint's by construction rather than by matching values by eye — the same
// correction the coach review screen already went through.
//
// ⚠️ THE ACTION SET IS EXW's, AND THERE IS NO "TO DISCUSS" BUTTON. EXW's row
// carries a comment saying exactly that — "why there is no To-discuss button" —
// because Accept resolves the row's open remarks in the SAME click. An earlier
// pass here added one anyway, which split into two steps what EXW deliberately
// made one, and put a control on the canvas duplicating the agenda's own job.
// The four actions are:
//
//   Accept         — sets `accepted` AND marks the row's open remarks discussed
//   Mark discussed — only when accepted with remarks still open (the fallback)
//   Modify         — inline edit (§7.4: direct overwrite, no confirm step)
//   Remove         — HIDDEN, not disabled, at the one-Objective floor
//
// ⚠️ `SummaryGroups` IS PASSED IN, not imported. The two apps keep it in
// different places — the CEO app has its own `_design/SummaryPanel`, the coach
// app aliases Strategy Canvas's shared copy — so importing it directly would
// break the byte-identical rule at the first `import` line. This is the same
// escape hatch `viewerRole` uses: app-specific things arrive as arguments.
//
// ⚠️ THE AGENDA IS `SummaryGroups`, like both sibling screens — not a stack of
// `eblr-section-comment` blocks. That class is for a section comment on the
// CANVAS and its `…-badge` is a two-word chip ("Section comment" in EXW); an
// earlier pass put whole Objective statements in it. See `annualOkrLiveAgenda`.
//
// ⚠️ NOTHING IS APPLIED OPTIMISTICALLY. §7.2: add / edit / delete emit a narrow
// delta and then a full `DETAILS` resync, and the contract says to re-render
// from that rather than hand-rolling patches. Status and discuss-marks get no
// resync — `objective_status_updated` + `remarks_updated` are the whole picture
// for those. Either way the server's echo is the confirmation.
// ═════════════════════════════════════════════════════════════════════════════

import "../../ExecutionBlueprint/ExecutionBlueprintLiveReview/ExecutionBlueprintLiveReview.scss";
// The composer's own stylesheet — `awr-*`. Both repos keep it at this same
// relative path, so this import survives the byte-identical rule.
import "../../ExecutionBlueprint/ExecutionBlueprintLiveReview/_shared/AddWigRow.scss";

const PARTICIPANT_ROLE_LABEL = { ceo: "CEO", coach: "Coach" };

const selectLive = (state) => ({
  objectives: state.objectives,
  roster: state.roster,
  remarks: state.remarks,
  remarksRemaining: state.remarksRemaining,
  joined: state.joined,
  error: state.error,
  annualOkrState: state.annualOkrState,
});

// §7.6 codes → what to actually tell someone mid-meeting. Branching on `code`,
// never on `message` — the contract says wording may change.
const ERROR_COPY = {
  live_review_not_started: "This live review hasn't been started yet.",
  not_authorized: "You don't have access to this live review.",
  objective_not_found:
    "That objective no longer exists — it may have just been removed.",
  wrong_state: "These OKRs have moved on. Refresh to catch up.",
  remarks_remaining: "Every agenda item has to be discussed before locking.",
  remark_not_found: "That agenda item has already been cleared.",
  already_locked: "These OKRs are already locked.",
  owner_required: "Choose an owner for the objective.",
  invalid_owner: "That owner isn't an active user of this company.",
  invalid_key_results: "An objective needs between one and three key results.",
  objective_floor_reached:
    "This is the last objective — there has to be at least one.",
};

// §8: 1–3 Key Results per Objective; the server refuses anything else with
// `invalid_key_results`.
const MIN_KEY_RESULTS = 1;
const MAX_KEY_RESULTS = 3;

const blankKeyResults = () => [{ statement: "", target: "" }];

const toKeyResultPayload = (keyResults) =>
  keyResults
    .filter((kr) => kr.statement.trim())
    .map((kr, index) => ({
      sequence: index + 1,
      statement: kr.statement.trim(),
      // §8: `unit` tells the server how to read `target`, which is a freeform
      // string. OTHER is the honest default for a target typed by hand in a
      // meeting — it does not claim the value is numeric.
      unit: "OTHER",
      // ⚠️ CARRIED, NOT AUTHORED. The composer stopped offering a target field
      // on 2026-09-21 — `target` is shown only on the post-lock scorecard, so
      // nobody sets one in a live review. `target` stays in the payload because
      // §8 declares the field; an edited Objective keeps whatever target it
      // already had, and a brand-new one sends "".
      target: (kr.target || "").trim(),
    }));

// The Objective form, shared by the row editor and the composer.
//
// ⚠️ IT USES EXW's `awr-*` CLASSES, from `AddWigRow.scss`. An earlier pass put
// bare `eblr-row-edit` inputs in a stack — that class is EXW's INLINE VALUE
// editor for a single field, not a form, so a three-field form built from it
// rendered as naked boxes with a Remove button floating beside each one. `awr-`
// is the composer's own set: labelled fields, a proper action row, and a
// trigger that looks like the card it will become.
const ObjectiveFieldset = ({
  idPrefix,
  statement,
  keyResults,
  onStatementChange,
  onKeyResultsChange,
  // ── Owner ────────────────────────────────────────────────────────────────
  // `[{ value, label }]` — react-select's shape, normalised by the caller.
  ownerOptions = [],
  ownerUserId = null,
  onOwnerChange,
  // ⚠️ THE OWNER IS EDITABLE ON BOTH FORMS NOW (2026-09-24). It was disabled
  // while editing because §7.1's documented `edit_annual_okr_objective`
  // payload carried no owner field, so a change would have been silently
  // dropped. The backend now accepts `owner_user_id` on that event, so an
  // Objective can be reassigned mid-meeting — which is the whole point of
  // having both parties in the room.
  //
  // Kept as a prop rather than hard-coded so a read-only surface can still
  // show the owner without offering to change it.
  ownerEditable = true,
}) => (
  <>
    <div className="awr-field">
      <label className="awr-label" htmlFor={`${idPrefix}-statement`}>
        Objective
      </label>
      <textarea
        id={`${idPrefix}-statement`}
        className="awr-input awr-input--area"
        rows={2}
        value={statement}
        placeholder="What has to be true by the end of the year?"
        onChange={(e) => onStatementChange(e.target.value)}
      />
    </div>

    <div className="awr-field awr-owner-field">
      <span className="awr-label">Owner</span>
      {/* ⚠️ `StandardSelect`, NOT A NATIVE `<select>`. The interview's
          ObjectiveDraftCard picks an owner with this exact component and
          variant; a bare native select rendered as the browser's own widget —
          the one control on this screen that looked like a different product.

          `isSearchable={false}` matches the interview: the roster is a handful
          of people, and a type-ahead over five names is friction. */}
      <StandardSelect
        variant="bordered"
        className="awr-owner-select"
        options={ownerOptions}
        // ⚠️ MATCHED LOOSELY, PASSED STRICTLY. `StandardSelect` finds the
        // selected option with `option.value === props.value` — a strict
        // compare — while the two sides of that comparison come from different
        // payloads and disagree on type: the roster sends `user_id` as a
        // number, and §5.6/§7.2 objective ids arrive as strings. `4 === "4"` is
        // false, so an Objective that HAS an owner opened the edit form with an
        // empty picker.
        //
        // Resolving here by string and handing back the option's OWN value
        // makes that strict compare succeed, whichever type either side uses.
        value={
          ownerOptions.find(
            (option) => String(option.value) === String(ownerUserId),
          )?.value ?? null
        }
        onChange={(option) => onOwnerChange?.(option?.value ?? null)}
        isSearchable={false}
        placeholder={
          ownerOptions.length
            ? "Choose an owner"
            : "No owner list available yet"
        }
        isDisabled={!ownerEditable || !ownerOptions.length}
        maxMenuHeight={200}
      />
      {!ownerEditable ? (
        <p className="awr-cap mb-0">This Objective&apos;s owner is fixed.</p>
      ) : null}
    </div>

    {/* ⚠️ ONE HEADING FOR THE SET, NOT ONE PER INPUT. Each row carried its own
        "Key Result 1" / "Key Result 2" / "Key Result 3" label, which read as
        three separate fields that happen to be named alike — and repeated the
        words three times on a form with four inputs in total. One "Key Results"
        label over the stack says the same thing once; the rows are ordered, so
        their position is their number. */}
    <div className="awr-field">
      <span className="awr-label">Key Results</span>

      {keyResults.map((kr, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="awr-kr-row" key={index}>
          <input
            id={`${idPrefix}-kr-${index}`}
            className="awr-input"
            type="text"
            value={kr.statement}
            // The visible label names the group, so each input carries its own
            // name for a screen reader, which reads them one at a time.
            aria-label={`Key Result ${index + 1}`}
            placeholder="How will you measure it?"
            onChange={(e) =>
              onKeyResultsChange(
                keyResults.map((item, i) =>
                  i === index ? { ...item, statement: e.target.value } : item,
                ),
              )
            }
          />
          {keyResults.length > MIN_KEY_RESULTS && (
            <button
              type="button"
              className="awr-kr-remove"
              aria-label={`Remove Key Result ${index + 1}`}
              onClick={() =>
                onKeyResultsChange(keyResults.filter((_, i) => i !== index))
              }
            >
              <i className="fa-regular fa-trash-can" aria-hidden="true" />
            </button>
          )}
          {/* ⚠️ NO TARGET INPUT. Removed 2026-09-21: a target is only ever
              read, on the post-lock scorecard. Asking for one in the middle of
              a live review put a number on screen that no surface between here
              and the lock would show back, and that §10 gives no way to track.
              The existing value rides along untouched in
              `toKeyResultPayload`. */}
        </div>
      ))}
    </div>

    {keyResults.length < MAX_KEY_RESULTS ? (
      <button
        type="button"
        className="awr-btn"
        onClick={() =>
          onKeyResultsChange([...keyResults, { statement: "", target: "" }])
        }
      >
        + Add a Key Result
      </button>
    ) : (
      // §8's cap, said where it applies rather than as a disabled control.
      <p className="awr-cap mb-0">
        Up to {MAX_KEY_RESULTS} key results per objective.
      </p>
    )}
  </>
);

// Who's in the room. §7.3: `participants` is ALWAYS a 2-item array with both
// roles present even before either has joined, so this renders two chips
// unconditionally and never has to handle a missing slot.
//
// The state text is visually hidden rather than deleted, exactly as EXW's is:
// the dot's colour is the whole signal otherwise, which leaves a screen-reader
// user with nothing and a red/green-colourblind reader with two dots they
// cannot tell apart. Clipped text stays in the accessibility tree.
export const PresenceStrip = ({ participants, viewerRole }) => (
  <div className="eblr-presence">
    {(participants || []).map((slot) => {
      const known = slot.name || (slot.user_id != null ? "Joined" : null);
      const isViewer = slot.role === viewerRole;
      const label = PARTICIPANT_ROLE_LABEL[slot.role] || slot.role;
      return (
        <span
          key={slot.role}
          className={`eblr-presence-chip${
            slot.connected ? " is-online" : " is-offline"
          }`}
          title={
            slot.connected
              ? `${label} is in the session`
              : known
                ? `${label} was here but isn't connected right now`
                : `${label} hasn't joined yet`
          }
        >
          <span className="eblr-presence-dot" />
          <span className="eblr-presence-role">
            {label}
            {isViewer ? " (you)" : ""}
          </span>
          {known && !isViewer && (
            <span className="eblr-presence-name">{known}</span>
          )}
          <span className="eblr-presence-state">
            {slot.connected ? "online" : known ? "offline" : "not joined"}
          </span>
        </span>
      );
    })}
  </div>
);

// One Objective in the session.
//
// ⚠️ THE STRUCTURE IS EXW's `LiveReviewRow`, not just its class names. An
// earlier pass reused the names against the wrong ELEMENTS — it made
// `eblr-row-left` the <button> when `eblr-row-header` is the button, so the
// statement rendered inside a grey bordered box — and invented
// `eblr-discuss-btn` where the real modifiers are `is-accept` / `is-discussed`
// / `is-remove`. Borrowing a class name only works when the element plays the
// same role.
// One confirm shell for both destructive moments in this screen. Built on
// `EbDialog` so it matches every other confirm in the product, and so it brings
// the portal, focus trap and Escape handling a hand-rolled popup would not.
const ConfirmDialog = ({ title, body, confirmLabel, onConfirm, onCancel }) => (
  <EbDialog
    isOpen
    size="sm"
    tone="danger"
    icon={<i className="fa-regular fa-trash-can" aria-hidden="true" />}
    title={title}
    onClose={onCancel}
    actions={[
      { label: "Cancel", variant: "ghost", onClick: onCancel },
      { label: confirmLabel, variant: "danger", onClick: onConfirm },
    ]}
  >
    {body}
  </EbDialog>
);

// The lock confirmation — Figma 34050-54935, shown to whichever party clicks.
//
// ⚠️ THE FIGMA'S BULLET LIST NAMES THINGS THAT DO NOT EXIST. It reads
// "Objectives, Key Results, Initiatives, Owners, and Contributors become
// read-only" and "All Company and Leader OKRs will be finalized". Initiatives
// and Contributors are dropped product-wide, and Leader OKRs are out of scope
// (§10 — company-level only). Naming them here would promise three features on
// the last screen before an irreversible action. The other three bullets are
// true and kept verbatim in meaning.
const LockConfirmDialog = ({ onConfirm, onCancel, busy }) => (
  <EbDialog
    isOpen
    size="sm"
    tone="primary"
    icon={<i className="fa-solid fa-lock" aria-hidden="true" />}
    title="Lock Annual OKRs?"
    // JK copy review O13. Neutral on purpose — this file is shared by both
    // apps, so "you and your coach" would be wrong for the coach. The third
    // bullet stops short of "each quarter" until the Q-REP link is confirmed.
    // One click from either side locks it (the only gate is every remark
    // discussed), and there is no unlock path.
    subtitle="This locks this year's Company OKRs, and it can't be undone. Lock them once you and the CEO agree."
    busy={busy}
    onClose={onCancel}
    actions={[
      { label: "Cancel", variant: "ghost", onClick: onCancel },
      {
        label: "Lock Annual OKRs",
        loadingLabel: "Locking…",
        variant: "primary",
        onClick: onConfirm,
        loading: busy,
      },
    ]}
  >
    <div className="ebd-panel">
      <span className="ebd-panel-label">After locking</span>
      <ul className="aolr-lock-list">
        <li>Objectives, key results and owners become read-only</li>
        <li>They become the company scorecard for the year</li>
        <li>
          The CEO updates progress on every objective and key result there
        </li>
      </ul>
    </div>
  </EbDialog>
);

const ObjectiveRow = ({
  objective,
  openRemarks,
  expanded,
  onToggle,
  onAccept,
  onMarkDiscussed,
  onEdit,
  onDelete,
  canDelete,
  // Only so the edit form can SHOW whose Objective this is — the owner is not
  // editable from a live review; see `ownerEditable` on the fieldset.
  ownerOptions = [],
}) => {
  const [editing, setEditing] = useState(false);
  const [draftStatement, setDraftStatement] = useState("");
  const [draftKrs, setDraftKrs] = useState(blankKeyResults());
  // The owner being edited. Seeded from the Objective each time the form
  // opens, so cancelling and reopening cannot carry a stale pick over.
  const [draftOwner, setDraftOwner] = useState(null);
  // Both destructive paths confirm: removing the Objective, and saving an edit
  // that drops Key Results. §7.4 makes both irreversible — a delete is a soft
  // delete with no undo in the UI, and an edit is a direct overwrite with no
  // history — and both land on the other person's screen immediately.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingSave, setConfirmingSave] = useState(null);

  const status = objective.review_status;
  // EXW's own condition, mirrored: a row the review ACCEPTED while leaving a
  // note — nothing left to accept, but a point still to talk through.
  const canMarkDiscussed = status === "accepted" && openRemarks.length > 0;

  const beginEdit = () => {
    setDraftStatement(objective.objective_statement || "");
    setDraftKrs(
      (objective.key_results || []).length
        ? objective.key_results.map((kr) => ({
            statement: kr.statement || "",
            target: kr.target || "",
          }))
        : blankKeyResults(),
    );
    setDraftOwner(objective.owner_user_id ?? null);
    setEditing(true);
  };

  const commitEdit = (keyResults) => {
    onEdit({
      objectiveStatement: draftStatement.trim(),
      keyResults,
      // Only when it actually changed: §7.4 is a direct overwrite, and an
      // unchanged field in the payload is a write nobody asked for.
      ...(draftOwner != null &&
      String(draftOwner) !== String(objective.owner_user_id)
        ? { ownerUserId: draftOwner }
        : {}),
    });
    setEditing(false);
    setConfirmingSave(null);
  };

  const saveEdit = () => {
    const keyResults = toKeyResultPayload(draftKrs);
    if (!draftStatement.trim() || keyResults.length < MIN_KEY_RESULTS) return;

    // ⚠️ Confirm only when the save DROPS Key Results. Adding or rewording one
    // is recoverable by editing again; removing one destroys wording nobody can
    // get back, and §7.4's overwrite carries no history. Counting rather than
    // comparing text, because a reworded KR is still the same KR.
    const before = (objective.key_results || []).length;
    if (keyResults.length < before) {
      setConfirmingSave({ keyResults, removed: before - keyResults.length });
      return;
    }
    commitEdit(keyResults);
  };

  return (
    <div className="eblr-row">
      {confirmingDelete ? (
        <ConfirmDialog
          title="Remove this Objective?"
          confirmLabel="Remove Objective"
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => {
            setConfirmingDelete(false);
            onDelete();
          }}
          body={
            <>
              {/* Quoted back, so the dialog is about a specific Objective
                  rather than "this item". */}
              <p className="ebd-quote">
                &ldquo;{objective.objective_statement || "Untitled Objective"}
                &rdquo;
              </p>
              <p>
                {/* Neutral: this file is shared by both apps, so "your
                    coach's screen" was wrong for the coach (G27). */}
                Its key results go with it, for everyone in the session. This
                can&apos;t be undone.
              </p>
            </>
          }
        />
      ) : null}

      {confirmingSave ? (
        <ConfirmDialog
          title={
            confirmingSave.removed === 1
              ? "Remove a Key Result?"
              : `Remove ${confirmingSave.removed} Key Results?`
          }
          confirmLabel="Save changes"
          onCancel={() => setConfirmingSave(null)}
          onConfirm={() => commitEdit(confirmingSave.keyResults)}
          body={
            <p>
              Saving replaces this objective for both of you straight away, and
              there is no history to go back to.
            </p>
          }
        />
      ) : null}

      <button className="eblr-row-header" onClick={onToggle}>
        <span className="eblr-row-left">
          <span className="eblr-row-label">
            {objective.objective_statement || "Untitled Objective"}
          </span>
        </span>
        <span className="eblr-row-right">
          {/* `focusable={false}`: the chip is inside the header <button>. Its
              hidden text joins the button's name instead. A live-added
              Objective is always freestanding (§7.4), so it shows "Not
              linked". */}
          {/* The Objective's Blueprint dimension, BEFORE the WIG link (by
              instruction, 2026-10-09): a fixed-width pill first keeps the link
              icons in one column down the list. Nothing when the payload
              doesn't carry `dimension`. */}
          <DimensionPill dimension={objective.dimension} />
          <WigLinkChip wigLabel={objective.wig_label} focusable={false} />
          {status ? (
            <span
              className={`eblr-status-pill ${
                status === "accepted"
                  ? "eblr-status-accepted"
                  : "eblr-status-discuss"
              }`}
            >
              {status === "accepted" ? "Accepted" : "To discuss"}
            </span>
          ) : null}
          <span className="eblr-row-chevron">
            <i
              className={`fa-solid ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`}
              aria-hidden="true"
            />
          </span>
        </span>
      </button>

      {expanded ? (
        <div className="eblr-row-body">
          {editing ? (
            <div className="awr-form">
              <ObjectiveFieldset
                idPrefix={`aok-edit-${objective.annual_objective_id}`}
                statement={draftStatement}
                keyResults={draftKrs}
                onStatementChange={setDraftStatement}
                onKeyResultsChange={setDraftKrs}
                // Reassignment happens here, in the room, with both parties
                // looking at it — see the note on `ownerEditable`.
                ownerOptions={ownerOptions}
                ownerUserId={draftOwner}
                onOwnerChange={setDraftOwner}
              />

              <div className="awr-actions">
                {/* ⚠️ "Replaces it for both of you" WAS HERE and is gone
                    (2026-09-24). §7.4 is still a direct overwrite with no
                    history and no confirmation step — but both parties are in
                    the room watching the same screen, so a caption explaining
                    that the other person will see the change is telling them
                    something they can see happening. */}
                <div className="awr-actions-right">
                  <button
                    type="button"
                    className="awr-btn"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="awr-btn awr-btn--primary"
                    onClick={saveEdit}
                    disabled={!draftStatement.trim()}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* ⚠️ THE SHARED BLOCK, on both sides of this session. The rows
                  were bold "1." prefixes on loose paragraphs here, a numbered
                  <ol> on the CEO's remarks panel and a third layout on the
                  coach's review — the same Objective looking like three
                  different objects depending on which screen it was on. See
                  `_shared/ObjectiveDetails.js`.

                  Owner comes from the §7 socket payload, which carries
                  `owner_name` on the objectives it broadcasts; the id is the
                  fallback for a row the server could not resolve. */}
              {objective.key_results?.length ? (
                <ObjectiveDetails
                  keyResults={objective.key_results}
                  ownerName={objective.owner_name}
                  ownerUserId={objective.owner_user_id}
                />
              ) : (
                <p className="eblr-row-value">No key results on this one.</p>
              )}

              {/* §7.4: both parties get all of these — "no role restriction
                  beyond having joined" — so none is gated on `viewerRole`. */}
              <div className="eblr-row-actions">
                {status !== "accepted" && (
                  <button
                    type="button"
                    className="eblr-act-btn is-accept"
                    // Accepts AND clears this row's open remarks in one click,
                    // which is precisely why there is no To-discuss button.
                    onClick={() => onAccept(openRemarks)}
                  >
                    Accept
                  </button>
                )}

                {canMarkDiscussed && (
                  <button
                    type="button"
                    className="eblr-act-btn is-discussed"
                    onClick={() =>
                      openRemarks.forEach((remark) => onMarkDiscussed(remark))
                    }
                  >
                    Mark discussed
                  </button>
                )}

                <button
                  type="button"
                  className="eblr-act-btn is-modify"
                  onClick={beginEdit}
                >
                  Modify
                </button>

                {/* Last and quietest: the only irreversible action here. HIDDEN
                    rather than disabled at the floor, as EXW does it — a run
                    must keep one Objective, and the last row's Remove would
                    otherwise be a dead control on every single-Objective run. */}
                {canDelete && (
                  <button
                    type="button"
                    className="eblr-act-btn is-remove"
                    onClick={() => setConfirmingDelete(true)}
                  >
                    Remove
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

// "+ Add an Objective" — the composer, opening IN the list rather than as a
// modal, for the reason EXW's `AddWigRow` gives: adding one happens
// mid-conversation with the other person watching the same screen, and the
// first question anyone asks is "what have we got already".
const AddObjectiveRow = ({ onAdd, ownerOptions = [] }) => {
  const [open, setOpen] = useState(false);
  const [statement, setStatement] = useState("");
  const [keyResults, setKeyResults] = useState(blankKeyResults());
  // ⚠️ THE OWNER IS CHOSEN, NOT ASSUMED, AND NOT PRE-FILLED (2026-09-24).
  // This first took `currentUserId` and sent it, so every Objective added in a
  // live review was silently owned by whoever happened to be typing. Seeding
  // the picker with them was the next version of the same problem: a default
  // that is wrong half the time still gets accepted, because a filled field
  // does not ask to be read.
  //
  // It opens empty, and Add stays disabled until someone is picked — §7.1
  // makes `owner_user_id` required precisely because it is a decision.
  const [owner, setOwner] = useState(null);

  const reset = () => {
    setStatement("");
    setKeyResults(blankKeyResults());
    setOwner(null);
    setOpen(false);
  };

  const submit = () => {
    const payload = toKeyResultPayload(keyResults);
    if (!statement.trim() || payload.length < MIN_KEY_RESULTS || !owner) return;
    onAdd({
      objectiveStatement: statement.trim(),
      ownerUserId: owner,
      // §7.1's add payload takes `{statement, unit, target}` without a
      // sequence — the server assigns it from the array order.
      keyResults: payload.map(({ statement: text, unit, target }) => ({
        statement: text,
        unit,
        target,
      })),
    });
    reset();
  };

  if (!open) {
    return (
      <button
        type="button"
        className="awr-trigger"
        onClick={() => setOpen(true)}
      >
        <span className="awr-trigger-plus">+</span>
        <span className="awr-trigger-text">Add an Objective</span>
      </button>
    );
  }

  return (
    <div className="awr-form">
      <ObjectiveFieldset
        idPrefix="aok-add"
        statement={statement}
        keyResults={keyResults}
        onStatementChange={setStatement}
        onKeyResultsChange={setKeyResults}
        ownerOptions={ownerOptions}
        ownerUserId={owner}
        onOwnerChange={setOwner}
      />

      <div className="awr-actions">
        {/* ⚠️ "Counts as agreed" WAS HERE and is gone (2026-09-24). It was true
            — §7.4 lands a live-added Objective CONFIRMED with an auto-ACCEPTED
            review row — but it explained a mechanism nobody had asked about, in
            a meeting where both parties are already talking to each other. */}
        <div className="awr-actions-right">
          <button type="button" className="awr-btn" onClick={reset}>
            Cancel
          </button>
          <button
            type="button"
            className="awr-btn awr-btn--primary"
            onClick={submit}
            // ⚠️ THE OWNER IS PART OF THE GATE. §7.1 refuses the whole call with
            // `owner_required` when it is missing, so blocking here turns a
            // round trip and a red error into a control that simply is not
            // ready yet.
            disabled={!statement.trim() || !owner}
          >
            Add Objective
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Joining skeleton ────────────────────────────────────────────────────────
//
// Mirrors the joined layout exactly: the same `eblr-body` two-column shell, a
// stack of Objective rows on the left and the "Meeting Agenda" panel on the
// right. Same classes, so it inherits the real widths, gaps and the column
// split — a skeleton built from its own layout classes drifts the moment the
// real one is adjusted.
//
// ⚠️ THE AGENDA'S HEADER IS REAL TEXT, not a shimmer block. It says "Meeting
// Agenda" in the joined state and it will say "Meeting Agenda" when the payload
// lands, so blanking it out would animate a certainty.
//
// Three rows, because a run has at least one Objective and typically a few —
// enough to read as a list, few enough not to imply a count. Each row's width
// is staggered so the stack doesn't look like a table.
const SKELETON_ROW_WIDTHS = ["78%", "92%", "64%"];

const LiveReviewSkeleton = () => (
  <div className="eblr-body" aria-busy="true">
    <div className="eblr-canvas">
      <div className="eblr-section">
        <div className="eblr-rows">
          {SKELETON_ROW_WIDTHS.map((width) => (
            <div className="aoklr-sk-row" key={width}>
              <span className="aoklr-sk-dot" />
              <span className="aoklr-sk-line" style={{ width }} />
              <span className="aoklr-sk-pill" />
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="eblr-agenda">
      <div className="eblr-agenda-header">Meeting Agenda</div>
      <div className="eblr-agenda-body">
        <div className="aoklr-sk-agenda">
          <span className="aoklr-sk-line aoklr-sk-line--sm" />
          <div className="aoklr-sk-card" />
          <div className="aoklr-sk-card" />
        </div>
      </div>
    </div>
  </div>
);

const AnnualOkrLiveReviewBody = ({
  uniqueId,
  viewerRole,
  // ⚠️ `currentUserId` WAS HERE AND IS GONE (2026-09-24). It existed only to
  // seed the Add-an-Objective owner picker, which now opens empty — see the
  // note in `AddObjectiveRow`. Both screens still pass it; the prop is simply
  // ignored, and can come out of them on the next pass.
  summaryGroupsComponent: SummaryGroups,
  // ⚠️ PASSED IN, like `SummaryGroups`, because the two apps keep
  // `StandardTooltip` at different paths and this file is byte-identical
  // across them. Falls back to a bare wrapper (native `title`) if a caller
  // doesn't pass one, so the disabled Lock button still explains itself (H03).
  tooltipComponent: Tooltip = null,
  onLocked,
}) => {
  const {
    objectives,
    roster,
    remarks,
    remarksRemaining,
    joined,
    error,
    annualOkrState,
  } = useAnnualOkrLiveReviewStore(selectLive);

  // One row open at a time, like EXW's — the canvas is a shared surface and two
  // open rows push the second person's focus off screen.
  const [expandedId, setExpandedId] = useState(null);
  // ⚠️ THE FIRST OBJECTIVE OPENS ITSELF, ONCE, ON BOTH SIDES. The meeting used
  // to start with every row shut, so the first thing either party did was click
  // to reveal what they had come to talk about.
  //
  // A ref, not state: it must not re-render, and it must survive the row being
  // collapsed — otherwise closing that first row would immediately re-open it
  // and the accordion could never be fully shut. It is also what stops the
  // §7.2 full resync (which arrives after every add, edit and delete) from
  // yanking the row open again mid-conversation.
  //
  // Both apps run this same file, so the CEO and the coach land on the same
  // open row rather than describing different screens to each other.
  const openedInitial = useRef(false);
  const [confirmingLock, setConfirmingLock] = useState(false);

  // Connect on mount, disconnect on unmount. The namespace is only ever open
  // while this screen is — leaving it connected would keep the room occupied
  // and keep delivering events to a store nothing reads.
  useEffect(() => {
    if (!uniqueId) return undefined;
    annualOkrLiveReviewStore.getState().connect({ uniqueId, viewerRole });
    return () => annualOkrLiveReviewStore.getState().disconnect();
  }, [uniqueId, viewerRole]);

  // ⚠️ §7.5 — LOCKING IS TERMINAL. No socket events fire for this run
  // afterwards, so the screen must leave rather than wait for anything else.
  // Driven off the broadcast, never off the click, so both participants move
  // together.
  useEffect(() => {
    if (annualOkrState === "LOCKED") onLocked?.();
  }, [annualOkrState, onLocked]);

  // Opens the first Objective as soon as the join lands. In an effect rather
  // than in `useState`'s initialiser because `objectives` is empty on the first
  // render — the room is still connecting.
  useEffect(() => {
    if (openedInitial.current || !objectives.length) return;
    openedInitial.current = true;
    setExpandedId(objectives[0].annual_objective_id);
  }, [objectives]);

  // ⚠️ NO All / To-discuss TABS. EXW's live review agenda has none — its only
  // `ebr-mode-toggle` is the Review/Blueprint switcher up in the HEADER, and
  // its agenda is header + `SummaryGroups` + footer, full stop. A tab pair here
  // was invented, and it was also redundant: the agenda only ever contains
  // remarks, `SummaryItem` already marks a discussed one, and the count that
  // matters is in the header and on the Lock button's tooltip.
  //
  // (The CEO's coach-review WAITING screen does have All / To discuss — that is
  // a different panel, `cr-tabs`, and it is EXW's.)
  // `objectives` goes in so the agenda keeps the board's order — see
  // `buildLiveAgenda`.
  const agendaGroups = useMemo(
    () => buildLiveAgenda(remarks, objectives),
    [remarks, objectives],
  );

  // ── Who can own an Objective ──────────────────────────────────────────────
  // §7.1 validates `owner_user_id` against the company's active roster and
  // refuses the whole add with `invalid_owner` otherwise, so this list must only
  // ever contain ids the server will accept.
  //
  // First choice is the roster on the state payload. It is not in the contract
  // yet (see the socket store's DETAILS handler), so the fallback is the owners
  // already on this board: every one of them is an id the server itself
  // assigned, which makes them safe by construction — but it cannot offer
  // someone who owns nothing yet, which is exactly the person a freestanding
  // Objective is most likely to need. **The roster is the real fix; flagged.**
  const ownerOptions = useMemo(() => {
    // `roster` comes from `annual_okr_roster` on this same namespace, asked
    // for right after the join — see the socket store. The owners already on
    // the board are the fallback for the window before it answers (and for a
    // server that does not implement the request yet): every one of those ids
    // was assigned by the server, so they are safe by construction, but they
    // cannot offer someone who owns nothing yet.
    const fromRoster = (roster || []).map((person) => ({
      // `{ value, label }` — react-select's shape, which `StandardSelect`
      // expects, and exactly how the interview feeds its own picker.
      value: person.user_id ?? person.id,
      label:
        person.name ||
        person.full_name ||
        `User #${person.user_id ?? person.id}`,
    }));
    if (fromRoster.length) return fromRoster;

    const seen = new Map();
    (objectives || []).forEach((objective) => {
      const id = objective.owner_user_id;
      if (id == null || seen.has(String(id))) return;
      seen.set(String(id), {
        value: id,
        label: objective.owner_name || `User #${id}`,
      });
    });
    return [...seen.values()];
  }, [roster, objectives]);

  // The still-open remarks for one Objective — what Accept clears in the same
  // click, and what "Mark discussed" falls back to.
  const openRemarksFor = (objectiveId) =>
    (remarks || []).filter(
      (remark) =>
        !remark.discussed &&
        String(remark.annual_objective_id) === String(objectiveId),
    );

  const store = annualOkrLiveReviewStore.getState;
  const canLock = joined && remarksRemaining === 0;

  // ⚠️ ONE ACTION, TWO EMITS — this is what makes a separate To-discuss button
  // unnecessary, and it is EXW's own design: accepting an Objective settles the
  // points raised against it, so the coach does not accept and then hunt the
  // agenda for the same row. `openRemarks` is passed in rather than re-derived
  // so the ids emitted are the same list the row was showing.
  const handleAccept = (objectiveId, openRemarks) => {
    store().setObjectiveStatus(objectiveId, "accepted");
    openRemarks.forEach((remark) =>
      store().markRemarkDiscussed(remark.remark_type, remark.id),
    );
  };

  // ⚠️ AN ERROR STILL GETS A MESSAGE, not a skeleton. A skeleton promises that
  // content is on its way; a failed join has nothing coming, and shimmering
  // forever would be a lie the user can only escape by reloading.
  if (!joined && error) {
    return (
      <div className="eblr-body">
        <div className="eblr-agenda-empty">
          <span className="eblr-agenda-empty-icon">
            <i
              className="fa-solid fa-triangle-exclamation"
              aria-hidden="true"
            />
          </span>
          <p className="eblr-agenda-empty-title">
            Couldn&apos;t join the session
          </p>
          <p className="eblr-agenda-empty-text">
            {ERROR_COPY[error.code] ||
              error.message ||
              "We couldn't join the live review. Please try again."}
          </p>
        </div>
      </div>
    );
  }

  // ⚠️ A SKELETON, NOT "Connecting… / Joining the session…". CLAUDE.md is
  // explicit that skeletons mirror the real layout rather than being a spinner
  // or a loading line, and this screen was the one place in the feature still
  // breaking that rule — a centred spinner with copy that narrated the socket's
  // internal state ("Connecting…" vs "Joining the session…"), which is a
  // distinction nobody outside this file can act on.
  //
  // It matters more here than on a normal screen. Both people arrive within a
  // second or two of each other, and a two-column board appearing out of a
  // single centred spinner reads as the page having changed underneath them.
  // The skeleton occupies the same two columns the real thing will, so the
  // handover is a fill rather than a replacement.
  if (!joined) {
    return <LiveReviewSkeleton />;
  }

  // ⚠️ `PresenceStrip` is EXPORTED, not rendered here. EXW puts it inline beside
  // the header title rather than on its own strip below it, and its comment
  // gives the reason: a strip costs ~32px of vertical space on a screen whose
  // height is bounded and whose two columns both want every pixel. The header
  // lives in each app's own screen file, so the strip goes there.
  return (
    <>
      <div className="eblr-body">
        <div className="eblr-canvas">
          <div className="eblr-section">
            {/* The same group heading the coach's Review Summary and the CEO's
                remarks panel carry — a coloured rail, the name, the count — so
                one list is labelled one way across every screen in this
                feature. */}
            <div className="okr-group-head">
              <span className="okr-group-rail" aria-hidden="true" />
              <span className="okr-group-name">Company OKRs</span>
              <span className="okr-group-count">{objectives.length}</span>
            </div>

            <div className="eblr-rows">
              {objectives.length === 0 ? (
                <div className="eblr-row-empty">No objectives yet.</div>
              ) : (
                objectives.map((objective) => {
                  const id = objective.annual_objective_id;
                  return (
                    <ObjectiveRow
                      key={id}
                      objective={objective}
                      openRemarks={openRemarksFor(id)}
                      expanded={expandedId === id}
                      onToggle={() =>
                        setExpandedId((prev) => (prev === id ? null : id))
                      }
                      onAccept={(openRemarks) => handleAccept(id, openRemarks)}
                      onMarkDiscussed={(remark) =>
                        store().markRemarkDiscussed(
                          remark.remark_type,
                          remark.id,
                        )
                      }
                      onEdit={(changes) =>
                        store().editObjective({
                          annualObjectiveId: id,
                          ...changes,
                        })
                      }
                      onDelete={() => store().deleteObjective(id)}
                      ownerOptions={ownerOptions}
                      // §7.4's floor guard. HIDDEN rather than disabled inside the row.
                      canDelete={objectives.length > 1}
                    />
                  );
                })
              )}

              {/* Last child of the rows stack, not a sibling of it: the
                  composer is the next slot in this list, and putting it outside
                  would separate it from the thing it adds to. */}
              <AddObjectiveRow
                // ⚠️ NO `currentUserId` ANY MORE. It used to seed the owner
                // picker with whoever was typing; the picker opens empty now
                // and Add waits for a real choice.
                ownerOptions={ownerOptions}
                onAdd={(draft) => store().addObjective(draft)}
              />
            </div>
          </div>
        </div>

        {/* ⚠️ The agenda is fed by the server's `remarks`, never derived
            client-side: the server owns which points are outstanding, and a
            local derivation would let the two people in the meeting see
            different counts while looking at each other. */}
        <div className="eblr-agenda">
          <div className="eblr-agenda-header">Meeting Agenda</div>

          <div className="eblr-agenda-body">
            {agendaGroups.length === 0 ? (
              <div className="eblr-agenda-empty">
                <span className="eblr-agenda-empty-icon">
                  <i className="fa-solid fa-check" aria-hidden="true" />
                </span>
                <p className="eblr-agenda-empty-title">Nothing to discuss</p>
                <p className="eblr-agenda-empty-text">
                  Nothing was flagged or commented on in the review, so there is
                  nothing holding up the lock.
                </p>
              </div>
            ) : (
              /* ⚠️ `SummaryGroups`, the same panel both sibling review screens
               use. Clicking a row opens the Objective it belongs to on the
               left, which is EXW's "click any agenda item to jump to it". */
              <SummaryGroups
                groups={agendaGroups}
                onSelectItem={(item) => {
                  const match = (objectives || []).find((objective) =>
                    String(item.id).startsWith(
                      `objective-${objective.annual_objective_id}-`,
                    ),
                  );
                  if (match) setExpandedId(match.annual_objective_id);
                }}
                // Both apps' `SummaryGroups` default this to their own
                // feature ("…in the blueprint" / "…on the canvas"), which is
                // wrong here.
                selectTitle="Show this in Company OKRs"
                emptyLabel="Nothing matches this filter."
              />
            )}
          </div>

          <div className="eblr-agenda-footer">
            {/* §7.5: the gate is `remarks_remaining === 0`, and the contract
                says the FE can disable locally on the same count rather than
                waiting for a failed attempt. A disabled control has to say
                why. */}
            {canLock ? (
              <button
                type="button"
                className="eblr-complete-btn"
                onClick={() => setConfirmingLock(true)}
              >
                Lock Annual OKRs
              </button>
            ) : (
              (() => {
                const reason = `${remarksRemaining} agenda item${
                  remarksRemaining === 1 ? "" : "s"
                } still to discuss`;
                // The wrapper span is what receives the hover: a disabled
                // button swallows the mouse events the tooltip listens for.
                const wrapped = (
                  <span
                    className="eblr-complete-btn-wrap"
                    title={Tooltip ? undefined : reason}
                  >
                    <button
                      type="button"
                      className="eblr-complete-btn"
                      disabled
                    >
                      Lock Annual OKRs
                    </button>
                  </span>
                );
                return Tooltip ? (
                  <Tooltip tooltTipContent={reason}>{wrapped}</Tooltip>
                ) : (
                  wrapped
                );
              })()
            )}
          </div>
        </div>
      </div>

      {confirmingLock ? (
        <LockConfirmDialog
          onCancel={() => setConfirmingLock(false)}
          onConfirm={() => {
            setConfirmingLock(false);
            store().lock();
          }}
        />
      ) : null}
    </>
  );
};

export default AnnualOkrLiveReviewBody;
